import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useRef, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import SelectComponent from "../../components/SelectComponent/SelectComponent";
import Addguest from "./Addguest";
import lang from "../../assets/lang.png";
import DateComponent from "./DateComponent";
import ReservationType from "./ReservationType";
import TimeComponent from "./TimeComponent";
import ReservationGroup from "./ReservationGroup";
import { useSelector } from "react-redux";
import { setIsBackToHoreca, setLanguageID, updateDefaultLanguage } from "../../store/slice/Addguest.slice";
import BookNowForm from "./BooknowForm";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import InactivityPopup from "../../utils/InActivityPopup";
import LanguagePopover from "../Component/LanguagePopover";
import { WidgetHeightProvider, useWidgetHeight } from "./widgetHeightContext";

dayjs?.extend(customParseFormat);

const WidgetContent = ({ data }) => {
  const {
    handleClose, widgetConfig, theme, dispatch,
    combinationLoading, reservTypeLoading,
  } = data;

  const { rootRef, onPopoverOpen, onPopoverClose } = useWidgetHeight();

  const { css } = useSelector((store) => store?.widgetSettings);
  const bookBtnCss = css?.find((item) => item?.cssTabFieldsGroupName === "Book button")?.fields;

  const hasImage = Boolean(widgetConfig?.settings?.imageFileName?.trim());
  const IMG_URL  = process.env.REACT_APP_API_LOCAL_IMG_URL;

  const {
    totalCount, selectedDate, reservType, time, reservGroup,
    defaultLanguage, languageWords, languageId, isBookNowOpen,
  } = useSelector((store) => store.addGuest);

  const { key, languageData, resType, widgetSetupId, deviceId } =
    useSelector((store) => store?.widgetSettings);

  const navigate = useNavigate();

  const [isAddGuest,    setIsAddGuest]    = useState(false);
  const [isDateOpen,    setIsDateOpen]    = useState(false);
  const [isResTypeOpen, setIsResTypeOpen] = useState(false);
  const [isTimeOpen,    setIsTimeOpen]    = useState(false);
  const [isGroupOpen,   setIsGroupOpen]   = useState(false);

  // Track whether any popover is open (used to manage overflow on mobile)
  const anyOpen = isAddGuest || isDateOpen || isResTypeOpen || isTimeOpen || isGroupOpen;

  const guestRef    = useRef(null);
  const dateRef     = useRef(null);
  const resTypeRef  = useRef(null);
  const timeRef     = useRef(null);
  const resGroupRef = useRef(null);

  const blur = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  // Close all popovers except the one keyed by `except`
  // Prevents two popovers being open simultaneously on mobile (causes UI breakage)
  const closeAll = useCallback((except) => {
    if (except !== "guest")   setIsAddGuest(false);
    if (except !== "date")    setIsDateOpen(false);
    if (except !== "resType") setIsResTypeOpen(false);
    if (except !== "time")    setIsTimeOpen(false);
    if (except !== "group")   setIsGroupOpen(false);
  }, []);

  const toggle = (setter, key, isCurrentlyOpen) => () => {
    blur();
    if (isCurrentlyOpen) {
      setter(false);
      onPopoverClose();
    } else {
      closeAll(key);
      setter(true);
      onPopoverOpen();
    }
  };

  const close = (setter) => () => {
    blur();
    setter(false);
    onPopoverClose();
  };

  const handleLanguageChange = (eventOrValue) => {
    const code = typeof eventOrValue === "string"
      ? eventOrValue : eventOrValue?.target?.value;
    if (!code) return;
    const selectedLang = languageData?.find((i) => i.langcode === code);
    if (!selectedLang) return;
    dispatch(setIsBackToHoreca(false));
    dispatch(updateDefaultLanguage(selectedLang.langcode));
    dispatch(setLanguageID(selectedLang.idlanguage));
    localStorage.setItem("widget", JSON.stringify({
      id: widgetSetupId, key, lang: selectedLang.langcode, resType,
    }));
  };

  const widgetPosition = widgetConfig?.settings?.widgetPosition ?? "Top";

  const commonSelectSx = {
    width: "100%",
    height: "72px",
    // Remove tap flash on mobile
    WebkitTapHighlightColor: "transparent",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme?.widget?.inputBorderColor}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme?.widget?.inputBorderColor}`,
    },
    "& .MuiOutlinedInput-input": {
      padding: 1,
      color: theme?.guest?.textColor,
      ml: 3,
      mt: 1,
      fontSize: { xs: "12px", md: "14px" },
    },
  };

  const isBookActive =
    reservType && typeof reservType === "object" &&
    Object.keys(reservType).length > 0 &&
    totalCount > 0 && selectedDate?.trim() !== "" && time?.trim() !== "";

  const imageBgSx = hasImage
    ? {
        backgroundImage: `url(${IMG_URL}/upload/Widgetsetup${widgetConfig.settings.imageFileName.replace(/\\/g, "/")})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        minHeight: widgetConfig?.settings?.imageHeight
          ? `${widgetConfig.settings.imageHeight}px`
          : "100vh",
      }
    : { backgroundColor: theme.widget?.mainBg };

  const panelSx = hasImage
    ? {
        position: "absolute", left: 0, right: 0, zIndex: 1,
        ...(widgetPosition === "Top"    && { top: 0 }),
        ...(widgetPosition === "Bottom" && { bottom: 0 }),
        ...(widgetPosition === "Middle" && { top: "50%", transform: "translateY(-50%)" }),
      }
    : { position: "relative" };

  return (
    <Box
      ref={rootRef}
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        // Allow popover content to overflow visibly without horizontal scroll
        overflowX: "hidden",
        ...imageBgSx,
      }}
    >
      {deviceId && <InactivityPopup languageWords={languageWords} />}

      <Box sx={{ ...panelSx, width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3 },
          }}
        >
          {/* Language + Close */}
          {languageData?.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <LanguagePopover
                  langIcon={lang}
                  languageData={languageData}
                  selectedLanguage={defaultLanguage}
                  onChange={handleLanguageChange}
                  theme={theme}
                  defaultLanguage={defaultLanguage}
                />
                {widgetConfig?.settings?.floatButtonActive &&
                  !widgetConfig?.settings?.kioskMode && (
                  <IconButton
                    size="large"
                    onClick={handleClose}
                    sx={{
                      backgroundColor: theme?.widgetButton?.closeBg,
                      // Ensure 44px minimum touch target on mobile
                      minWidth: { xs: 44, sm: "auto" },
                      minHeight: { xs: 44, sm: "auto" },
                      "&:hover": { backgroundColor: theme?.widgetButton?.closeHover },
                    }}
                  >
                    <CloseIcon sx={{ color: theme?.widgetButton?.closeText }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

          {totalCount >= widgetConfig?.reservationRequestMin && (
            <Box
              sx={{
                color: theme?.widget?.reservGrpTextClr,
                mb: 1,
                fontSize: { xs: "13px", sm: "14px" },
              }}
            >
              {languageWords?.reservationGroupText}
            </Box>
          )}

          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
              color: theme?.widget?.titleTextClr,
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {languageWords?.widgetHeading}
          </Typography>

          {/*
            FIX: position: "relative" on Grid container establishes a stacking
            context so absolutely-positioned popover children (Addguest,
            DateComponent, etc.) are contained within this layer and don't
            escape the viewport or overlap unexpected elements on mobile.
          */}
          <Grid
            container
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems="stretch"
            sx={{ position: "relative" }}
          >

            {/* Guests */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SelectComponent
                label={languageWords?.Guest}
                value={totalCount}
                onClick={toggle(setIsAddGuest, "guest", isAddGuest)}
                ref={guestRef}
                sx={commonSelectSx}
              />
              <Addguest
                isOpen={isAddGuest}
                Ref={guestRef}
                hasImage={hasImage}
                handleClose={close(setIsAddGuest)}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SelectComponent
                label={languageWords?.date}
                value={selectedDate?.trim() !== "" ? selectedDate : "DD-MM-YYYY"}
                onClick={toggle(setIsDateOpen, "date", isDateOpen)}
                ref={dateRef}
                disabled={totalCount <= 0}
                sx={commonSelectSx}
              />
              <DateComponent
                isOpen={isDateOpen}
                Ref={dateRef}
                handleClose={close(setIsDateOpen)}
              />
            </Grid>

            {/* Reservation Type */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SelectComponent
                label={languageWords?.reserveType}
                value={
                  reservType &&
                  typeof reservType === "object" &&
                  Object.keys(reservType).length > 0
                    ? reservType.reservationtypeLanguages?.find(
                        (l) => l.fieldname === "name" || l.idlanguage === languageId
                      )?.data
                    : "Type"
                }
                onClick={toggle(setIsResTypeOpen, "resType", isResTypeOpen)}
                ref={resTypeRef}
                disabled={!selectedDate?.trim()}
                sx={commonSelectSx}
              />
              <ReservationType
                isOpen={isResTypeOpen}
                Ref={resTypeRef}
                isLoading={reservTypeLoading}
                handleClose={close(setIsResTypeOpen)}
              />
            </Grid>

            {/* Time */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SelectComponent
                label={languageWords?.Time}
                value={time?.trim() !== "" ? time?.substring(0, 5) : "HH:MM"}
                onClick={toggle(setIsTimeOpen, "time", isTimeOpen)}
                ref={timeRef}
                disabled={
                  !(
                    reservType &&
                    typeof reservType === "object" &&
                    Object.keys(reservType).length > 0
                  )
                }
                sx={commonSelectSx}
              />
              <TimeComponent
                isOpen={isTimeOpen}
                Ref={timeRef}
                handleClose={close(setIsTimeOpen)}
              />
            </Grid>

            {/* Reservation Group */}
            {widgetConfig?.settings?.showReservationGroup && (
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <SelectComponent
                  label={languageWords?.reserveGroup}
                  value={
                    reservGroup &&
                    typeof reservGroup === "object" &&
                    Object.keys(reservGroup).length > 0
                      ? reservGroup?.name
                      : "Group"
                  }
                  onClick={toggle(setIsGroupOpen, "group", isGroupOpen)}
                  ref={resGroupRef}
                  disabled={!time?.trim()}
                  sx={commonSelectSx}
                />
                <ReservationGroup
                  isOpen={isGroupOpen}
                  Ref={resGroupRef}
                  handleClose={close(setIsGroupOpen)}
                />
              </Grid>
            )}

            {/* Book Now */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  height: "72px",
                
                  backgroundColor: isBookActive
                    ? bookBtnCss?.BookNowButtonBackgroundColorActive
                    : bookBtnCss?.BookNowButtonBackgroundColor,
                  color: isBookActive
                    ? bookBtnCss?.BookNowButtonTextColorActive
                    : bookBtnCss?.BookNowButtonTextColor,
                  fontWeight: "bold",
                  fontSize: { xs: "13px", sm: "14px" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "none",
                  borderRadius: "8px",
                  // Prevent layout shift on tap (mobile)
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                  "&:hover": { boxShadow: "none" },
                  "&:active": { transform: "scale(0.98)" },
                }}
                onClick={() => {
                  if (isBookActive && !combinationLoading) navigate("/bookNow");
                }}
                disabled={combinationLoading}
              >
                {languageWords?.Booknow}
                {reservType?.totalCost > 0 &&
                  totalCount < widgetConfig?.reservationRequestMin && (
                  <Box
                    component="span"
                    fontSize="12px"
                    fontWeight="300"
                    mt={0.25}
                  >
                    €{parseFloat(reservType?.totalCost).toFixed(2)}
                  </Box>
                )}
              </Button>
            </Grid>

          </Grid>

          {isBookNowOpen && <BookNowForm />}
        </Box>
      </Box>
    </Box>
  );
};

const Widget = ({ data, heightChange }) => {
  const hasImage = Boolean(data?.widgetConfig?.settings?.imageFileName?.trim());
  return (
    <WidgetHeightProvider onHeightChange={heightChange} disabled={hasImage}>
      <WidgetContent data={data} heightChange={heightChange} />
    </WidgetHeightProvider>
  );
};

export default Widget;
